import { useEffect } from "react";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import {
  trackCoframeExperimentAssigned,
  type CoframeExperimentAssignedProps,
} from "src/tracking/events/coframe-experiment-assigned";

const SENT_STORAGE_KEY = "cf_assignments_sent";
const SENT_STORAGE_CAP = 100;

let sentKeys: Set<string> | null = null;

function getSentKeys(): Set<string> {
  if (!sentKeys) {
    try {
      const stored: unknown = JSON.parse(
        window.localStorage.getItem(SENT_STORAGE_KEY) || "[]"
      );
      sentKeys = new Set(
        Array.isArray(stored)
          ? stored.filter((key): key is string => typeof key === "string")
          : []
      );
    } catch {
      sentKeys = new Set();
    }
  }
  return sentKeys;
}

function markSent(key: string) {
  const keys = getSentKeys();
  keys.add(key);
  try {
    window.localStorage.setItem(
      SENT_STORAGE_KEY,
      JSON.stringify(Array.from(keys).slice(-SENT_STORAGE_CAP))
    );
  } catch {
    // Storage unavailable — dedup falls back to in-memory only.
  }
}

function dedupKey(item: CoframeExperimentAssignedProps): string {
  return `${item.coframe_user_token}:${item.coframe_experiment_id}:${item.coframe_variant_id}`;
}

export function resetCoframeDedupForTesting() {
  sentKeys = null;
}

function Coframe() {
  // Subscribe to "treatmentApplied" via bulkCallbackV2 — only the V2 payload
  // includes experiment/variant slugs. `callback` and `bulkCallback` deliver
  // the legacy V1 shape (IDs only). Coframe re-fires the callback on SPA
  // navigation and re-application, so send each user/experiment/variant
  // assignment only once (persisted in localStorage).
  useEffect(() => {
    (window as any).CFQ = (window as any).CFQ || [];
    (window as any).CFQ.push({
      subscribe: "treatmentApplied",
      bulkCallbackV2: (data: CoframeExperimentAssignedProps[]) => {
        data.forEach((item) => {
          if (!item?.coframe_experiment_id) return;
          const key = dedupKey(item);
          if (getSentKeys().has(key)) return;
          // trackEvent silently no-ops without htevents — don't mark as sent.
          if (!window.htevents || typeof window.htevents.track !== "function")
            return;
          void trackCoframeExperimentAssigned(item);
          markSent(key);
        });
      },
    });
    (window as any).CFQ.push({ emit: "pageHydrated" });
  }, []);

  // Feature flag control
  const { coframeEnabled } = useFeatureFlags();
  useEffect(() => {
    (window as any).CFQ = (window as any).CFQ || [];
    (window as any).CFQ.push({ emit: { featureFlagSet: coframeEnabled } });
  }, [coframeEnabled]);
  return null;
}

export default Coframe;
